
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building, Check, ChevronRight, Search, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { useBusinesses } from '@/hooks/useBusinesses';
import { useBusinessPagination } from '@/hooks/useBusinessPagination';
import { useBusinessSearch } from '@/hooks/useBusinessSearch';
import { useSubscription } from '@/hooks/useSubscription';
import { ModernInput } from '@/components/ui/modern-input';
import { ModernDropdown } from '@/components/ui/modern-dropdown';
import { Icon } from '@/components/icons';
import ModernButton from '@/components/dashboard/ModernButton';
import { useToast } from '@/components/ui/use-toast';
import { errorMessage as getErrorMessage } from '@/lib/errorMessage';

const BusinessList = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const { pageUrl, offset, nextPage: goToNextPage, previousPage: goToPreviousPage, resetPagination } = useBusinessPagination();
  const { draftSearch, setDraftSearch, searchQuery, applySearch, clearSearch } = useBusinessSearch(resetPagination);
  const searchRef = React.useRef<HTMLInputElement>(null);

  const { data, isLoading, isFetching, isError, error } = useBusinesses({
    search: searchQuery,
    plan: planFilter,
    status: statusFilter,
    pageUrl,
  });
  const { subscriptionPlans, isLoadingSubscriptionPlans } = useSubscription({
    fetchCurrent: false,
    fetchAllPlans: true,
    fetchInvoice: false,
  });
  const { toast } = useToast();

  const businesses = data?.items ?? [];
  const nextPage = data?.next ?? null;
  const prevPage = data?.previous ?? null;
  const totalBusinesses = data?.count ?? businesses.length;

  useEffect(() => {
    if (!isError || !error) return;

    const errorMessage = getErrorMessage(error, 'Failed to load businesses');

    toast({
      title: 'Error loading businesses',
      description: errorMessage,
      variant: 'destructive',
    });
  }, [isError, error, toast]);

  const handlePageChange = (url: string | null, direction: 'next' | 'previous') => {
    if (!url || isFetching) return;
    if (direction === 'next') {
      goToNextPage(url, businesses.length);
    } else {
      goToPreviousPage(url);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const statusOptions = [
    { value: 'all', label: 'All Status', description: 'Show all businesses' },
    { value: 'active', label: 'Active', description: 'Active businesses' },
    { value: 'inactive', label: 'Disabled', description: 'Disabled businesses' }
  ];

  const planOptions = useMemo(() => {
    const base = [
      { value: 'all', label: 'All Plans', description: 'Show all plans' },
    ];

    if (!subscriptionPlans || subscriptionPlans.length === 0) {
      return [
        ...base,
        { value: 'free', label: 'Free', description: 'Free plan businesses' },
        { value: 'standard', label: 'Standard', description: 'Standard plan businesses' },
        { value: 'enterprise', label: 'Enterprise', description: 'Enterprise plan businesses' },
      ];
    }

    const uniquePlanNames = Array.from(
      new Set(
        subscriptionPlans
          .map(plan => plan?.name?.trim())
          .filter((name): name is string => Boolean(name))
      )
    );

    const dynamicOptions = uniquePlanNames.map(name => ({
      value: name,
      label: name,
      description: `Businesses on the ${name} plan`,
    }));

    return [...base, ...dynamicOptions];
  }, [subscriptionPlans]);

  useEffect(() => {
    if (planFilter === 'all') return;
    const isValid = planOptions.some(option => option.value === planFilter);
    if (!isValid) {
      setPlanFilter('all');
      resetPagination();
    }
  }, [planOptions, planFilter, resetPagination]);

  return (
    <div className="min-h-screen bg-neutral-100/50 dark:bg-[hsla(0,0%,0%,0.95)]">
      <div className="container max-w-7xl mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
              Business Management
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400 mt-1">
              Manage all businesses on your platform
            </p>
          </div>
              <div className="relative w-full max-w-md ml-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 h-4 w-4" />
                <ModernInput
                  ref={searchRef}
                  aria-label="Search businesses"
                  placeholder="Type business name and hit enter"
                  value={draftSearch}
                  onChange={(e) => setDraftSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      applySearch();
                    }
                  }}
                  className="pl-9 pr-10"
                />
                {draftSearch && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => {
                      clearSearch();
                      searchRef.current?.focus();
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </button>
                )}
              </div>
        </div>

        {/* Business List */}
        <Card className="bg-transparent dark:bg-transparent border-0 rounded-3xl !pl-0">
          <CardHeader className='px-0 pt-0'>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-3">
                <ModernDropdown
                  value={statusFilter}
                  onValueChange={(value) => {
                    setStatusFilter(value);
                    resetPagination();
                  }}
                  options={statusOptions}
                  placeholder="Filter by status"
                  className="w-40"
                />
                <ModernDropdown
                  value={planFilter}
                  onValueChange={(value) => {
                    setPlanFilter(value);
                    resetPagination();
                  }}
                  options={planOptions}
                  placeholder="Filter by plan"
                  className="w-40"
                />
              </div>
              <CardDescription>
                {isLoading ? 'Loading...' : `${totalBusinesses} businesses total`}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className='px-0'>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Card key={n} className="p-6 bg-neutral-50 dark:bg-neutral-800/30 border-0 rounded-2xl">
                    <div className="flex items-center space-x-4">
                      <Skeleton className="h-12 w-12 rounded-xl" />
                      <div className="flex-1 space-y-3">
                        <Skeleton className="h-5 w-[250px]" />
                        <div className="flex gap-4">
                          <Skeleton className="h-4 w-[120px]" />
                          <Skeleton className="h-4 w-[80px]" />
                          <Skeleton className="h-4 w-[100px]" />
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-6 w-16 rounded-full" />
                        <Skeleton className="h-6 w-16 rounded-full" />
                      </div>
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-8 w-8 rounded-full" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {isError || businesses.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Building className="h-12 w-12 mx-auto mb-4 text-neutral-400" />
                    <p className="text-lg font-medium mb-2">
                      {isError ? 'Unable to load businesses' : 'No businesses found'}
                    </p>
                    <p className="text-sm">
                      {isError
                        ? (error instanceof Error ? error.message : 'An unexpected error occurred')
                        : (searchQuery || statusFilter !== 'all' || planFilter !== 'all'
                            ? 'No businesses match your search criteria.'
                            : 'No businesses available.')}
                    </p>
                  </div>
                ) : (
                  businesses.map((business) => (
                    <Link
                      key={business.id}
                      to={`/businesses/${business.id}`}
                      className="block"
                    >
                      <Card className="bg-white/70 dark:bg-neutral-800/70 rounded-xl p-4 dark:border-neutral-600/50 cursor-pointer hover:bg-white dark:hover:bg-neutral-800 transition-colors duration-200 animate-fade-in">
                        <div className="flex items-center gap-4">
                          {/* Business Icon */}
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center">
                            <Icon type='plain' name={`Team`} color='hsl(var(--primary))' className="h-5 w-5 text-white" />
                          </div>

                          {/* Business Info */}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 truncate text-md capitalize">
                              {business.name || 'Business Name'}
                            </h3>
                            
                          </div>

                          {/* Plan and Status Badges */}
                          <div className="flex items-center gap-3">
                            
                            
                            <Badge 
                              variant={
                                business.status.toLowerCase() === 'active' ? 'success' :  
                                'destructive'
                              }
                              className="text-xs px-3 py-1 rounded-full"
                            >
                              {business.status.toLowerCase() === 'active' ? (
                                <Check className="h-3 w-3 mr-1" />
                              ) : business.status.toLowerCase() === 'trial' ? (
                                <div className="h-2 w-2 rounded-full bg-blue-500 mr-1" />
                              ) : (
                                <></>
                              )}
                              {business.status.toLowerCase() === 'inactive' ? 'Disabled' : business.status === 'None' ? 'New' : business.status}
                            </Badge>
                          </div>

                          {/* Created Date */}
                          <div className="text-sm text-neutral-500 dark:text-neutral-400 min-w-fit">
                            {new Date(business.created).toLocaleDateString()}
                          </div>

                          {/* Action Icon */}
                          <ChevronRight className="h-5 w-5 text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-300 transition-colors" />
                        </div>
                      </Card>
                    </Link>
                  ))
                )}
                {businesses.length > 0 && (
                  <div className="flex flex-col gap-3 pt-6 border-t border-neutral-200 dark:border-neutral-800 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      Showing {(offset + 1).toLocaleString()}&ndash;{(offset + businesses.length).toLocaleString()} of {totalBusinesses.toLocaleString()} businesses
                    </p>
                    <div className="flex gap-2">
                      <ModernButton
                        variant="outline"
                        disabled={!prevPage || isLoading || isFetching}
                        onClick={() => handlePageChange(prevPage, 'previous')}
                      >
                        Previous
                      </ModernButton>
                      <ModernButton
                        variant="outline"
                        disabled={!nextPage || isLoading || isFetching}
                        onClick={() => handlePageChange(nextPage, 'next')}
                      >
                        Next
                      </ModernButton>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BusinessList;
